import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscribe to the real-time /api/events/stream (SSE) feed for unified
 * customer↔staff chat. Invokes `onEvent(payload)` for every thread event:
 *   - thread_message { thread_id, sender_side, preview, ... }
 *   - thread_typing  { thread_id, user_id, name, side }
 *   - thread_read    { thread_id, reader_id, reader_side }
 * Auto-reconnects on drop.
 */
export default function useChatStream(onEvent, { enabled = true } = {}) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !API_URL) return undefined;
    let es;
    let closed = false;
    let reconnectTimer = null;

    const connect = () => {
      if (closed) return;
      es = new EventSource(`${API_URL}/api/events/stream`, { withCredentials: true });
      const dispatch = (e) => {
        try {
          const data = JSON.parse(e.data);
          handlerRef.current && handlerRef.current(data);
        } catch { /* ignore malformed frames */ }
      };
      ['thread_message', 'thread_typing', 'thread_read'].forEach((t) =>
        es.addEventListener(t, dispatch));
      es.onerror = () => {
        if (es && es.readyState === EventSource.CLOSED && !closed) {
          clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(reconnectTimer);
      if (es) es.close();
    };
  }, [enabled]);
}
